import { useEffect, useRef } from 'react';

const SpaceBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const setSize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setSize();

    // ── 배경 별 (원거리, layer 0.10~0.35) ─────────────────
    const bgStars = Array.from({ length: 600 }, () => ({
      xN: Math.random(), yN: Math.random(),
      r: Math.random() * 0.9 + 0.15,
      op: Math.random() * 0.55 + 0.15,
      twSpeed: Math.random() * 0.012 + 0.003,
      twPhase: Math.random() * Math.PI * 2,
      layer: 0.10 + Math.random() * 0.25,
      hue: Math.floor(Math.random() < 0.70 ? 0 : Math.random() < 0.67 ? 1 : 2),
    }));

    // ── 전경 별 (근거리, layer 0.55~1.00) ─────────────────
    const fgStars = Array.from({ length: 120 }, () => ({
      xN: Math.random(), yN: Math.random(),
      r: Math.random() * 1.8 + 0.8,
      op: Math.random() * 0.7 + 0.3,
      twSpeed: Math.random() * 0.025 + 0.008,
      twPhase: Math.random() * Math.PI * 2,
      layer: 0.55 + Math.random() * 0.45,
    }));

    // ── 밀키웨이 (헤더 존, layer 0.08) ────────────────────
    let mwStars = [];
    const buildMW = () => {
      mwStars = Array.from({ length: 2200 }, () => {
        const t  = Math.random();
        const arcY = 80 - Math.sin(t * Math.PI) * 22;
        const u = Math.random(), v = Math.random();
        const gauss = Math.sqrt(-2 * Math.log(u + 1e-9)) * Math.cos(2 * Math.PI * v);
        const spread = 55 + Math.abs(t - 0.5) * 30;
        const yAbs = arcY + gauss * spread * 0.45;

        const roll = Math.random();
        let cr, cg, cb;
        if      (roll < 0.60) { cr=180+Math.random()*40; cg=200+Math.random()*40; cb=245; }
        else if (roll < 0.85) { const bv=220+Math.random()*35; cr=cg=cb=bv; }
        else if (roll < 0.95) { cr=245; cg=228+Math.random()*20; cb=175+Math.random()*50; }
        else                  { cr=245; cg=155+Math.random()*45; cb=125+Math.random()*55; }

        const bright = Math.max(0, 1 - Math.abs(yAbs - arcY) / 55);
        return {
          xN: t, yAbs,
          r: Math.random() * 0.55 + 0.12,
          op: (Math.random() * 0.45 + 0.15) * (0.4 + bright * 0.6),
          cr: Math.min(255,cr), cg: Math.min(255,cg), cb: Math.min(255,cb),
          twSpeed: Math.random() * 0.015 + 0.003,
          twPhase: Math.random() * Math.PI * 2,
        };
      });
    };
    buildMW();

    // ── 유성 ────────────────────────────────────────────────
    const shooting = [];
    const spawnShooting = () => {
      if (Math.random() < 0.5) return;
      shooting.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height * 0.5,
        len: Math.random() * 130 + 70,
        speed: Math.random() * 12 + 7,
        op: 0.9,
        angle: Math.PI / 5 + (Math.random() * 0.3 - 0.15),
      });
    };
    const shootTimer = setInterval(spawnShooting, 3200);

    // ── 마우스 시차 (작은 ambient 효과) ────────────────────
    let mX = 0, mY = 0;

    // ── 드래그 팬 (우주비행사 시점) ────────────────────────
    let panX = 0, panY = 0;
    let tPanX = 0, tPanY = 0;   // 목표 pan (smooth lerp용)
    let isDrag = false;
    let lastX = 0, lastY = 0;
    let velX = 0, velY = 0;     // 관성 velocity

    const SKIP = 'BUTTON,A,INPUT,SELECT,TEXTAREA';
    const shouldSkip = (el) => !!el.closest(SKIP);

    // 마우스
    const onDown = (e) => {
      if (shouldSkip(e.target)) return;
      isDrag = true;
      lastX = e.clientX; lastY = e.clientY;
      velX = velY = 0;
      document.body.style.cursor = 'grabbing';
    };
    const onMove = (e) => {
      mX = (e.clientX / window.innerWidth  - 0.5) * 28;
      mY = (e.clientY / window.innerHeight - 0.5) * 14;
      if (!isDrag) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      velX = dx; velY = dy;
      tPanX += dx * 1.6;
      tPanY += dy * 1.6;
      lastX = e.clientX; lastY = e.clientY;
    };
    const onUp = () => {
      isDrag = false;
      document.body.style.cursor = '';
    };

    // 터치
    const onTouchStart = (e) => {
      if (shouldSkip(e.target)) return;
      const t = e.touches[0];
      isDrag = true; lastX = t.clientX; lastY = t.clientY; velX = velY = 0;
    };
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!isDrag) return;
      const dx = t.clientX - lastX; const dy = t.clientY - lastY;
      velX = dx; velY = dy;
      tPanX += dx * 1.6; tPanY += dy * 1.6;
      lastX = t.clientX; lastY = t.clientY;
    };
    const onTouchEnd = () => { isDrag = false; };

    const onResize = () => { setSize(); buildMW(); };

    window.addEventListener('resize',     onResize);
    window.addEventListener('mousedown',  onDown);
    window.addEventListener('mousemove',  onMove);
    window.addEventListener('mouseup',    onUp);
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove',  onTouchMove,  { passive: true });
    window.addEventListener('touchend',   onTouchEnd);

    // ── 렌더 ────────────────────────────────────────────────
    let animId;
    const wrap = (v, size) => ((v % size) + size) % size;

    const animate = () => {
      const w = canvas.width, h = canvas.height;

      // 관성 & smooth lerp
      if (!isDrag) {
        velX *= 0.93; velY *= 0.93;
        tPanX += velX; tPanY += velY;
      }
      panX += (tPanX - panX) * 0.13;
      panY += (tPanY - panY) * 0.13;

      ctx.clearRect(0, 0, w, h);

      // 1. 성운
      const nX = panX * 0.02, nY = panY * 0.02;
      [
        { cx:0.22, cy:0.38, r:0.44, c0:'rgba(56,139,253,0.07)',  c1:'rgba(26,75,150,0.03)'  },
        { cx:0.70, cy:0.58, r:0.38, c0:'rgba(124,58,237,0.06)',  c1:'rgba(76,29,149,0.02)'  },
        { cx:0.88, cy:0.18, r:0.30, c0:'rgba(13,148,136,0.07)',  c1:'rgba(6,71,67,0.02)'    },
        { cx:0.50, cy:0.78, r:0.32, c0:'rgba(37,99,235,0.05)',   c1:'rgba(30,58,138,0.02)'  },
      ].forEach(({ cx, cy, r, c0, c1 }) => {
        const gx = cx*w + nX, gy = cy*h + nY;
        const g = ctx.createRadialGradient(gx, gy, 0, gx, gy, r*w);
        g.addColorStop(0, c0); g.addColorStop(1, c1);
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      });

      // 2. 밀키웨이 글로우
      const mwBase = ctx.createLinearGradient(0, 0, 0, 200);
      mwBase.addColorStop(0,   'rgba(80,120,255,0.00)');
      mwBase.addColorStop(0.3, 'rgba(80,120,255,0.05)');
      mwBase.addColorStop(0.6, 'rgba(60,100,220,0.04)');
      mwBase.addColorStop(1,   'rgba(40,80,200,0.00)');
      ctx.fillStyle = mwBase; ctx.fillRect(0, 0, w, 200);

      const cGX = w*0.5 + panX*0.08;
      const cG  = ctx.createRadialGradient(cGX, 72, 0, cGX, 72, w*0.28);
      cG.addColorStop(0, 'rgba(140,180,255,0.13)');
      cG.addColorStop(0.3,'rgba(96,140,255,0.06)');
      cG.addColorStop(1,  'rgba(0,0,0,0)');
      ctx.fillStyle = cG; ctx.fillRect(0, 0, w, 200);

      const cRX = w*0.72 + panX*0.08;
      const cR  = ctx.createRadialGradient(cRX, 60, 0, cRX, 60, w*0.18);
      cR.addColorStop(0, 'rgba(45,212,191,0.08)');
      cR.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = cR; ctx.fillRect(0, 0, w, 160);

      // 3. 배경 별
      bgStars.forEach(s => {
        s.twPhase += s.twSpeed;
        const tw = Math.sin(s.twPhase) * 0.25 + 0.75;
        const px = wrap(s.xN*w + panX*s.layer + mX*s.layer*0.4, w);
        const py = wrap(s.yN*h + panY*s.layer + mY*s.layer*0.3, h);
        const [r,g,b] = s.hue===0 ? [195,215,255] : s.hue===1 ? [255,240,200] : [255,180,160];
        ctx.beginPath();
        ctx.arc(px, py, s.r, 0, Math.PI*2);
        ctx.fillStyle = `rgba(${r},${g},${b},${s.op*tw})`;
        ctx.fill();
      });

      // 4. 밀키웨이 미세별
      mwStars.forEach(s => {
        s.twPhase += s.twSpeed;
        const tw = Math.sin(s.twPhase) * 0.2 + 0.8;
        const px = wrap(s.xN*w + panX*0.08, w);
        const py = s.yAbs + panY*0.06;
        ctx.beginPath();
        ctx.arc(px, py, s.r, 0, Math.PI*2);
        ctx.fillStyle = `rgba(${s.cr},${s.cg},${s.cb},${s.op*tw})`;
        ctx.fill();
      });

      // 5. 전경 밝은 별 + 십자 글로우
      fgStars.forEach(s => {
        s.twPhase += s.twSpeed;
        const tw = Math.sin(s.twPhase) * 0.3 + 0.7;
        const px = wrap(s.xN*w + panX*s.layer + mX*s.layer*0.7, w);
        const py = wrap(s.yN*h + panY*s.layer + mY*s.layer*0.5, h);
        const op = s.op * tw;

        const halo = ctx.createRadialGradient(px, py, 0, px, py, s.r*5);
        halo.addColorStop(0, `rgba(200,225,255,${op*0.22})`);
        halo.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = halo;
        ctx.beginPath(); ctx.arc(px, py, s.r*5, 0, Math.PI*2); ctx.fill();

        ctx.beginPath(); ctx.arc(px, py, s.r, 0, Math.PI*2);
        ctx.fillStyle = `rgba(220,235,255,${op})`; ctx.fill();

        if (s.r > 1.4) {
          ctx.save();
          ctx.globalAlpha = op * 0.35;
          ctx.strokeStyle = 'rgba(200,220,255,1)';
          ctx.lineWidth = 0.6;
          const gl = s.r * 10;
          ctx.beginPath(); ctx.moveTo(px-gl,py); ctx.lineTo(px+gl,py); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(px,py-gl); ctx.lineTo(px,py+gl); ctx.stroke();
          ctx.restore();
        }
      });

      // 6. 유성
      for (let i = shooting.length - 1; i >= 0; i--) {
        const ss = shooting[i];
        ss.x += Math.cos(ss.angle) * ss.speed;
        ss.y += Math.sin(ss.angle) * ss.speed;
        ss.op -= 0.016;
        if (ss.op <= 0) { shooting.splice(i, 1); continue; }
        const tail = ctx.createLinearGradient(
          ss.x - Math.cos(ss.angle)*ss.len, ss.y - Math.sin(ss.angle)*ss.len,
          ss.x, ss.y
        );
        tail.addColorStop(0, 'rgba(200,230,255,0)');
        tail.addColorStop(1, `rgba(210,235,255,${ss.op*0.9})`);
        ctx.beginPath();
        ctx.moveTo(ss.x-Math.cos(ss.angle)*ss.len, ss.y-Math.sin(ss.angle)*ss.len);
        ctx.lineTo(ss.x, ss.y);
        ctx.strokeStyle = tail; ctx.lineWidth = 1.4; ctx.stroke();
      }

      animId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animId);
      clearInterval(shootTimer);
      window.removeEventListener('resize',     onResize);
      window.removeEventListener('mousedown',  onDown);
      window.removeEventListener('mousemove',  onMove);
      window.removeEventListener('mouseup',    onUp);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove',  onTouchMove);
      window.removeEventListener('touchend',   onTouchEnd);
      document.body.style.cursor = '';
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0, left: 0,
        width: '100%', height: '100%',
        zIndex: -1,
        pointerEvents: 'none',
      }}
    />
  );
};

export default SpaceBackground;
